// What the input box shows while dictating (brain-multimodal, D7).
//
// Asked for on 2026-09-28: with only the microphone icon changing, dictating
// looked as if nothing was happening. So the box shows the voice as it comes --
// bars that move with how loud it is, newest on the right, like a voice note --
// the time so far, and a «Stop» button that is hard to miss.
//
// The bars are read from the live microphone with the Web Audio API. Where the
// browser does not have it, they stay flat and everything else still works.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Square } from 'lucide-react'
import { Button } from '@/components/ui/button'

const BARS = 32
const EVERY_MS = 50

function loudness(analyser: AnalyserNode, samples: Uint8Array<ArrayBuffer>): number {
  analyser.getByteTimeDomainData(samples)
  let sum = 0
  for (const sample of samples) {
    const centred = (sample - 128) / 128
    sum += centred * centred
  }
  // Speech sits low on this scale: stretched so a normal voice fills the bar.
  return Math.min(1, Math.sqrt(sum / samples.length) * 4)
}

function useLevels(stream: MediaStream): number[] {
  const [levels, setLevels] = useState<number[]>(() => Array(BARS).fill(0))

  useEffect(() => {
    const Context = window.AudioContext
    if (!Context) return
    const context = new Context()
    const analyser = context.createAnalyser()
    analyser.fftSize = 512
    context.createMediaStreamSource(stream).connect(analyser)
    const samples = new Uint8Array(analyser.fftSize)
    const timer = window.setInterval(() => {
      const level = loudness(analyser, samples)
      setLevels((previous) => [...previous.slice(1), level])
    }, EVERY_MS)
    return () => {
      window.clearInterval(timer)
      void context.close()
    }
  }, [stream])

  return levels
}

function useElapsed(): string {
  const [seconds, setSeconds] = useState(0)
  useEffect(() => {
    const timer = window.setInterval(() => setSeconds((s) => s + 1), 1000)
    return () => window.clearInterval(timer)
  }, [])
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

export function DictationWave({ stream, onStop }: { stream: MediaStream; onStop: () => void }) {
  const { t } = useTranslation()
  const levels = useLevels(stream)
  const elapsed = useElapsed()

  return (
    <div className="flex items-center gap-2 px-4 pt-3.5 pb-1.5" role="status" aria-label={t('brain.dictado.escuchando')}>
      <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-destructive" aria-hidden />
      <span className="w-10 shrink-0 text-sm tabular-nums text-muted-foreground">{elapsed}</span>
      <div className="flex h-8 min-w-0 flex-1 items-center gap-0.5" aria-hidden>
        {levels.map((level, i) => (
          <span
            key={i}
            className="w-1 flex-1 rounded-full bg-primary transition-[height] duration-75"
            style={{ height: `${Math.max(8, level * 100)}%` }}
          />
        ))}
      </div>
      <Button type="button" size="sm" variant="destructive" onClick={onStop}>
        <Square className="h-3.5 w-3.5" />
        {t('brain.dictado.detener')}
      </Button>
    </div>
  )
}
